import { createAsyncThunk, createSelector, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { conns, connsPublic } from '@cfg/axiosConfig.tsx';
import { isAxiosError } from 'axios';
import type { SiteFeatures, PlanCapabilities, SiteInfo } from '@app-types/site.types.ts';
import type { TemplateDefinitionOverrides } from '@eshop/templates/resolveCatalogueTemplate';
import type { ItemCardSize } from '@eshop/templates';
import type { Address } from '@eshop/types/location.types.ts';
import type { ImageInfo } from '@app-types/commons/images.types.ts';
import type { BrandingFaviconBundle } from '@app-types/branding.types.ts';
import { buildBrandingFaviconLinks } from '@tools/head/applyFaviconFromSettings.ts';
import { parseWarningsHeader, type SettingsWarningsPayload } from '@tools/socialLinks.ts';
import type { ColorsParameterMap } from '@theme/types/colors.types.tsx';
import { logInfo } from '@tools/LogsHandler.tsx';

export interface WhatsappNumber {
  label: string;
  phone_e164: string;
  phone_verified_at?: string | null;
}

export interface SiteLocation {
  label: string;
  address: string;
  lat: number;
  lng: number;
  maps_url?: string | null;
  maps_embed_code?: string | null;
  place_id?: string | null;
  notes?: string | null;
}

export interface OpeningRange {
  start: string;
  end: string;
}

export interface ClosurePeriod {
  label: string;
  start_date: string;
  end_date: string;
}

export interface Holiday {
  label: string;
  date: string;
}

export interface OpeningHours {
  mon: OpeningRange[];
  tue: OpeningRange[];
  wed: OpeningRange[];
  thu: OpeningRange[];
  fri: OpeningRange[];
  sat: OpeningRange[];
  sun: OpeningRange[];
  closures?: ClosurePeriod[];
  holidays?: Holiday[];
  timezone?: string | null;
}

export interface CatalogLabelSettings {
  in_stock_badge?: string | null;
  backorder_badge?: string | null;
  backorder_message?: string | null;
  out_of_stock_badge?: string | null;
  out_of_stock_message?: string | null;
  unknown_badge?: string | null;
  unknown_message?: string | null;
  notify_me_cta?: string | null;
  blocked_message?: string | null;
  oversale_message?: string | null;
}

export interface CatalogFlagSettings {
  hide_backorder_label?: boolean | null;
}

export interface CatalogueBannerVisibility {
  active?: boolean | null;
  start?: string | null;
  end?: string | null;
  category_ids?: number[] | null;
}

export interface CatalogueBannerSettings {
  hero?: CatalogueBannerVisibility | null;
  top?: CatalogueBannerVisibility | null;
  between_rows?: CatalogueBannerVisibility | null;
  bottom?: CatalogueBannerVisibility | null;
}

export type OrderTemplateTrigger =
  | 'order_created'
  | 'order_paid'
  | 'payment_failed';
export type OrderTemplateChannel = 'email' | 'whatsapp' | 'pdf';

export interface OrderTemplateBranding {
  logo_url?: string | null;
  primary_color?: string | null;
  secondary_color?: string | null;
}

export interface OrderTemplateChannelConfig {
  channel: OrderTemplateChannel;
  body: string;
  enabled?: boolean;
  subject?: string | null;
  format?: 'markdown' | 'text';
}

export interface OrderNotificationTemplateSetting {
  key: string;
  name?: string | null;
  description?: string | null;
  trigger: OrderTemplateTrigger;
  enabled?: boolean;
  branding?: OrderTemplateBranding | null;
  channels: OrderTemplateChannelConfig[];
}

export interface PaymentReminderConfig {
  initial_delay_minutes: number;
  interval_hours: number;
  max_attempts: number;
}

export interface ActivateBrandingFaviconResult {
  url: string;
  legacy_url: string;
  bundle: BrandingFaviconBundle;
}

export interface UploadBrandingFaviconArgs {
  file: Blob;
  filename?: string;
}

interface UploadBrandingFaviconError {
  message: string;
}

export interface SiteSettings {
  default_offer_scope?: 'eshop' | 'erp' | 'both' | null;
  name: string | null;
  domain: string | null;
  headline: string | null;
  subheadline: string | null;
  description: string | null;
  keywords: string[] | null;
  timezone: string | null;
  logo: ImageInfo | null;
  dash_banner?: ImageInfo | null;
  og_default_image: ImageInfo | null;
  contact_email: string | null;
  seo_visible: boolean | null;

  canonical_base_url: string | null;
  currency_code: string | null;
  store_address: Address | null;
  locations?: SiteLocation[] | null;
  google_maps_links?: string[] | null;
  opening_hours?: OpeningHours | null;
  whatsapp_numbers?: WhatsappNumber[] | null;
  landline_numbers?: string[] | null;
  enable_whatsapp_fab?: boolean | null;
  whatsapp_fab_message?: string | null;
  contact_verification_required?: boolean | null;
  public_under_construction?: boolean | null;
  facebook_url?: string | null;
  instagram_url?: string | null;
  tiktok_url?: string | null;
  youtube_url?: string | null;
  twitter_url?: string | null;
  ga_measurement_id?: string | null;
  gtm_id?: string | null;
  facebook_pixel_id?: string | null;
  recaptcha_site_key?: string | null;
  analytics_exports_enabled?: boolean | null;
  analytics_consent_mode?: 'implicit' | 'opt_in' | 'opt_out' | null;
  facebook_access_token_masked?: string | null;
  logo_image_id?: number | null;
  dash_banner_image_id?: number | null;
  og_default_image_id?: number | null;
  catalogue_design?: string | null;
  eshop_block_sales_without_stock?: boolean | null;
  eshop_guest_checkout_enabled?: boolean | null;
  eshop_show_conditional_promotion_prices?: boolean | null;
  erp_pos_mix_eshop_suggestions?: boolean | null;
  erp_appointments_customer_phone_required?: boolean | null;
  erp__appointments__groups?: boolean | null;
  erp__appointments__groups__domain?: boolean | null;
  erp__appointments__groups__shadow_projection?: boolean | null;
  erp__appointments__groups__agenda?: boolean | null;
  erp__appointments__groups__operation?: boolean | null;
  erp__appointments__groups__billing?: boolean | null;
  erp__appointments__groups__backfill?: boolean | null;
  erp_appointments_generic_staff_enabled?: boolean | null;
  erp_appointments_generic_space_enabled?: boolean | null;
  erp_appointments_warn_on_unauthorized_staff?: boolean | null;
  erp_appointments_warn_on_unauthorized_space?: boolean | null;
  erp_appointments_generic_staff_id?: number | null;
  erp_appointments_generic_space_id?: number | null;
  erp_subscriptions_initial_billing_threshold_days?: number | null;
  erp_stock_track_new_items_by_default?: boolean | null;
  catalogue_item_card_size?: ItemCardSize | null;
  catalogue_max_items_per_row?: number | null;
  catalog_labels?: CatalogLabelSettings | null;
  catalog_flags?: CatalogFlagSettings | null;
  catalogue_template_overrides?: TemplateDefinitionOverrides | null;
  site_code?: string | null;
  code?: string | null;
  branding_favicon?: BrandingFaviconBundle | null;
  order_notification_templates?: OrderNotificationTemplateSetting[] | null;
  payment_reminder?: PaymentReminderConfig | null;
  payment_provider_default_image_ids?: Partial<Record<string, number>> | null;
  catalogue_banners?: CatalogueBannerSettings | null;
  site_colors?: ColorsParameterMap | null;
  site_colors_harmony?: {
    harmony: 'monochromatic' | 'analogous' | 'complementary' | 'triadic' | 'custom';
    colorCount: 1 | 2 | 3;
    baseColor1: string;
    baseColor2?: string;
    baseColor3?: string;
  } | null;
  site_typography?: SiteTypography | null;
}

export type SiteFontKey =
  | 'inherit'
  | 'inter'
  | 'roboto'
  | 'open_sans'
  | 'lato'
  | 'montserrat'
  | 'poppins'
  | 'playfair_display'
  | 'merriweather'
  | 'anton'
  | 'audiowide'
  | 'didot'
  | 'fang_song'
  | 'inria_sans'
  | 'bookman_old_style'
  | 'optima_italic';

export interface SiteTypography {
  heading_font: SiteFontKey;
  subheading_font: SiteFontKey;
  body_font: SiteFontKey;
}

interface SettingsState {
  site: SiteSettings | null;
  status: 'idle' | 'loading' | 'failed';
  apps: string[];                 // apps habilitadas para el sitio
  infoStatus: 'idle' | 'loading' | 'failed';
  siteInfo: SiteInfo | null;
  features: SiteFeatures;
  erpEnabled: boolean;            // valor precalculado para consultas rápidas
  planCapabilities: PlanCapabilities;
  etag: string | null;
  lastModified: string | null;
  currentDomain: string | null;
}

const initialState: SettingsState = {
  site: null,
  status: 'idle',
  apps: [],
  infoStatus: 'idle',
  siteInfo: null,
  features: {},
  erpEnabled: false,
  planCapabilities: {} as PlanCapabilities,
  etag: null,
  lastModified: null,
  currentDomain: null,
};

const getHeaderValue = (headers: unknown, key: string): string | null => {
  if (!headers || typeof headers !== 'object') {
    return null;
  }

  const target = key.toLowerCase();
  for (const [headerKey, value] of Object.entries(headers as Record<string, unknown>)) {
    if (headerKey.toLowerCase() !== target) {
      continue;
    }
    if (Array.isArray(value)) {
      return value[0] ?? null;
    }
    if (typeof value === 'string') {
      return value;
    }
  }
  return null;
};

type FetchSiteSettingsResult =
  | { status: 'ok'; settings: SiteSettings; etag: string | null; lastModified: string | null }
  | { status: 'not-modified' };

export interface UpdateSiteSettingsResult {
  settings: SiteSettings;
  warnings: SettingsWarningsPayload | null;
}

type SettingsRootState = { settings: SettingsState };

export const fetchSiteSettings = createAsyncThunk<FetchSiteSettingsResult, void, { state: SettingsRootState }>(
  'settings/fetchSite',
  async (_arg, { getState }) => {
    const { etag } = getState().settings;
    const headers: Record<string, string> = {};
    if (etag) {
      headers['If-None-Match'] = etag;
    }

    const response = await connsPublic<SiteSettings>('settings:public').get(
      {},
      {
        headers,
        validateStatus: (status: number) => status === 200 || status === 304,
      },
    );

    if (response.status === 304) {
      return { status: 'not-modified' } as const;
    }

    const nextEtag = getHeaderValue(response.headers, 'etag');
    const lastModified = getHeaderValue(response.headers, 'last-modified');

    return {
      status: 'ok',
      settings: response.data as SiteSettings,
      etag: nextEtag,
      lastModified,
    } as const;
  },
);

export const fetchAdminSettings = createAsyncThunk('settings/fetchAdmin', async () => {
  const { data } = await conns('settings:update').get();
  return data as SiteSettings;
});

export const updateSiteSettings = createAsyncThunk<UpdateSiteSettingsResult, Partial<SiteSettings>>(
  'settings/updateSite',
  async (payload) => {
    logInfo('   ## Settings ## : payload: ', payload);
    const response = await conns('settings:update').patch(payload);
    const headerValue = getHeaderValue(response.headers, 'x-settings-warnings');
    const warnings = parseWarningsHeader(headerValue);
    return {
      settings: response.data as SiteSettings,
      warnings,
    };
  },
);

export const fetchSiteInfo = createAsyncThunk('settings/fetchInfo', async () => {
  const { data } = await connsPublic<SiteInfo>('site:info').get();
  return data;
});

export const activateBrandingFavicon = createAsyncThunk<ActivateBrandingFaviconResult, { groupId: string }>(
  'settings/activateBrandingFavicon',
  async ({ groupId }) => {
    const { data } = await conns<ActivateBrandingFaviconResult>('settings:update', ['favicon', 'set-active']).post(
      { group_id: groupId },
    );
    return data;
  },
);

export const uploadBrandingFavicon = createAsyncThunk<
  ActivateBrandingFaviconResult,
  UploadBrandingFaviconArgs,
  { rejectValue: UploadBrandingFaviconError }
>(
  'settings/uploadBrandingFavicon',
  async ({ file, filename }, { rejectWithValue }) => {
    const formData = new FormData();
    const effectiveName = filename && filename.trim() ? filename : 'favicon.png';
    formData.append('file', file, effectiveName);

    try {
      const { data } = await conns<ActivateBrandingFaviconResult>('settings:update', ['favicon']).post(formData);
      return data;
    } catch (error) {
      if (isAxiosError(error)) {
        const rawDetail =
          typeof error.response?.data === 'string'
            ? error.response?.data
            : (error.response?.data as { detail?: unknown })?.detail ?? error.message;
        const message = typeof rawDetail === 'string' && rawDetail.trim()
          ? rawDetail
          : 'Error al subir el favicon';
        return rejectWithValue({ message });
      }
      throw error;
    }
  },
);

const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    mergeSiteSettingsLocal(state, action: PayloadAction<Partial<SiteSettings>>) {
      const current = state.site ?? ({} as SiteSettings);
      state.site = {
        ...current,
        ...action.payload,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSiteSettings.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchSiteSettings.fulfilled, (state, action) => {
        state.status = 'idle';
        if (action.payload.status === 'ok') {
          const currentHost = typeof window !== 'undefined' ? window.location.host : null;

          if (state.currentDomain && state.currentDomain !== currentHost) {
            state.site = null;
            state.apps = [];
            state.siteInfo = null;
            state.features = {};
            state.erpEnabled = false;
            state.planCapabilities = {} as PlanCapabilities;
            state.etag = null;
            state.lastModified = null;
            state.infoStatus = 'idle';
          }

          state.currentDomain = currentHost;

          const colors = (action.payload.settings.site_colors as any)?.colors;
          // logInfo('>>> fetchSiteSettings.fulfilled - DEBUG RAW PAYLOAD site_colors:', JSON.stringify(action.payload.settings.site_colors, null, 2));

          state.site = { ...action.payload.settings };
          if (colors) {
            state.site = { ...state.site, site_colors: colors };
            // logInfo('>>> fetchSiteSettings.fulfilled - extracted colors and updated state:', JSON.stringify(colors, null, 2));
          } else {
            logInfo('>>> fetchSiteSettings.fulfilled - Using raw site_colors (no .colors property found). State:', JSON.stringify(state.site.site_colors, null, 2));
          }
          state.etag = action.payload.etag;
          state.lastModified = action.payload.lastModified;
        }
        logInfo('>>> fetchSiteSettings.fulfilled - final state.site:', state.site);
      })
      .addCase(fetchSiteSettings.rejected, (state) => {
        state.status = 'failed';
      })
      .addCase(fetchAdminSettings.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchAdminSettings.fulfilled, (state, action) => {
        state.status = 'idle';
        state.site = { ...action.payload };
        state.etag = null;
        state.lastModified = null;
      })
      .addCase(fetchAdminSettings.rejected, (state) => {
        state.status = 'failed';
      })
      .addCase(updateSiteSettings.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(updateSiteSettings.fulfilled, (state, action) => {
        state.status = 'idle';
        const current = state.site ?? ({} as SiteSettings);

        // Merge: mantené lo que ya estaba y sobreescribí sólo lo que vino en el PATCH
        state.site = {
          ...current,
          ...(action.payload.settings ?? {}),
        };

        logInfo('   ## Settings ## : state.site: ', state.site);

        // Después de un PATCH, tiramos el cache de ETag/Last-Modified
        state.etag = null;
        state.lastModified = null;
      })
      .addCase(updateSiteSettings.rejected, (state) => {
        state.status = 'failed';
      })
      .addCase(fetchSiteInfo.pending, (state) => {
        state.infoStatus = 'loading';
      })
      .addCase(fetchSiteInfo.fulfilled, (state, action) => {
        state.infoStatus = 'idle';
        state.siteInfo = action.payload;
        state.apps = action.payload.apps ?? [];
        state.features = action.payload.features ?? {};
        state.erpEnabled = Boolean(state.features.erp);
        state.planCapabilities =
          action.payload.plan_capabilities ?? ({} as PlanCapabilities);
      })
      .addCase(fetchSiteInfo.rejected, (state) => {
        state.infoStatus = 'failed';
        state.siteInfo = null;
        state.features = {};
        state.erpEnabled = false;
      })
      .addCase(activateBrandingFavicon.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(activateBrandingFavicon.fulfilled, (state, action) => {
        state.status = 'idle';
        if (state.site) {
          state.site = { ...state.site, branding_favicon: action.payload.bundle };
        }
      })
      .addCase(activateBrandingFavicon.rejected, (state) => {
        state.status = 'failed';
      })
      .addCase(uploadBrandingFavicon.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(uploadBrandingFavicon.fulfilled, (state, action) => {
        state.status = 'idle';
        if (state.site) {
          state.site = { ...state.site, branding_favicon: action.payload.bundle };
        }
      })
      .addCase(uploadBrandingFavicon.rejected, (state) => {
        state.status = 'failed';
      });
  },
});

export const { mergeSiteSettingsLocal } = settingsSlice.actions;

export const selectSiteApps = (s: { settings: SettingsState }) => s?.settings?.apps ?? [];
export const selectSiteInfoStatus = (s: { settings: SettingsState }) => s?.settings?.infoStatus ?? 'idle';
export const selectSiteInfo = (s: { settings: SettingsState }) => s?.settings?.siteInfo ?? null;
export const selectErpEnabled = (s: { settings: SettingsState }) => Boolean(s?.settings?.erpEnabled);
export const selectSiteSettings = (s: { settings: SettingsState }) => s?.settings?.site ?? null;
export const selectCurrentDomain = (s: { settings: SettingsState }) => s?.settings?.currentDomain ?? null;
export const selectSiteTitle = createSelector(
  [selectSiteInfo],
  (siteInfo) => {
    const siteName = typeof siteInfo?.name === 'string' ? siteInfo.name.trim() : '';
    return siteName || null;
  },
);
export const selectBrandingFavicon = createSelector(
  [selectSiteSettings],
  (site) => site?.branding_favicon ?? null,
);

const selectSettingsCacheHints = createSelector(
  [
    (s: { settings: SettingsState }) => s.settings.etag ?? null,
    (s: { settings: SettingsState }) => s.settings.lastModified ?? null,
  ],
  (etag, lastModified) => ({ etag, lastModified }),
);

export const selectBrandingFaviconLinks = createSelector(
  [selectBrandingFavicon, selectSettingsCacheHints],
  (bundle, cacheHints) => buildBrandingFaviconLinks(bundle, cacheHints),
);
export const selectSiteStatus = (s: { settings: SettingsState }) => s?.settings?.status ?? 'idle';
export const selectSiteFeatures = (s: { settings: SettingsState }) => s?.settings?.features ?? {};
const normalizeSettingsFlag = (value: boolean | null | undefined, fallback = true): boolean =>
  value == null ? fallback : Boolean(value);

export const selectGroupAppointmentsEnabled = (s: { settings: SettingsState }) =>
  (s?.settings?.apps ?? []).includes('erp__appointments__groups');

const selectGroupAppointmentsLayerEnabled = (
  s: { settings: SettingsState },
  layerKey: string,
): boolean => {
  const masterEnabled = selectGroupAppointmentsEnabled(s);
  if (!masterEnabled) {
    return false;
  }
  const features = selectSiteFeatures(s);
  if (Object.prototype.hasOwnProperty.call(features, layerKey)) {
    return Boolean(features[layerKey]);
  }
  const site = s.settings.site as Record<string, unknown> | null;
  if (site && Object.prototype.hasOwnProperty.call(site, layerKey)) {
    return Boolean(site[layerKey]);
  }
  return true;
};

export const selectGroupAppointmentsDomainEnabled = (s: { settings: SettingsState }) =>
  selectGroupAppointmentsLayerEnabled(s, 'erp__appointments__groups__domain');

export const selectGroupAppointmentsAgendaEnabled = (s: { settings: SettingsState }) =>
  selectGroupAppointmentsLayerEnabled(s, 'erp__appointments__groups__agenda');

export const selectGroupAppointmentsOperationEnabled = (s: { settings: SettingsState }) =>
  selectGroupAppointmentsLayerEnabled(s, 'erp__appointments__groups__operation');

export const selectGroupAppointmentsBillingEnabled = (s: { settings: SettingsState }) =>
  selectGroupAppointmentsLayerEnabled(s, 'erp__appointments__groups__billing');

export const selectGroupAppointmentsBackfillEnabled = (s: { settings: SettingsState }) =>
  selectGroupAppointmentsLayerEnabled(s, 'erp__appointments__groups__backfill');

export const selectSiteFeatureFlags = createSelector(
  [selectSiteFeatures, selectGroupAppointmentsEnabled],
  (features, groupAppointmentsEnabled) => ({
    ...(features ?? {}),
    erp__appointments__groups: groupAppointmentsEnabled,
  }),
);

export const resolveAppointmentsWarningsEnabled = (
  staffFlag?: boolean | null,
  spaceFlag?: boolean | null,
): boolean => normalizeSettingsFlag(staffFlag) && normalizeSettingsFlag(spaceFlag);

export const selectGuestCheckoutSetting = (s: { settings: SettingsState }) =>
  s.settings.site?.eshop_guest_checkout_enabled ?? null;
export const selectContactVerificationRequired = (s: { settings: SettingsState }) =>
  s?.settings?.site?.contact_verification_required ?? true;
export const selectBlockSalesWithoutStockSetting = (s: { settings: SettingsState }) =>
  Boolean(s.settings.site?.eshop_block_sales_without_stock);
export const selectPlanCapabilities = (s: { settings: SettingsState }) => s.settings.planCapabilities;
export const selectAppointmentsCustomerPhoneRequired = (s: { settings: SettingsState }) =>
  Boolean(s.settings.site?.erp_appointments_customer_phone_required);
export const selectAppointmentsGenericStaffEnabled = (s: { settings: SettingsState }) =>
  normalizeSettingsFlag(s.settings.site?.erp_appointments_generic_staff_enabled);
export const selectAppointmentsGenericSpaceEnabled = (s: { settings: SettingsState }) =>
  normalizeSettingsFlag(s.settings.site?.erp_appointments_generic_space_enabled);
export const selectAppointmentsGenericStaffId = (s: { settings: SettingsState }) =>
  s.settings.site?.erp_appointments_generic_staff_id ?? null;
export const selectAppointmentsGenericSpaceId = (s: { settings: SettingsState }) =>
  s.settings.site?.erp_appointments_generic_space_id ?? null;
export const selectAppointmentsWarnOnUnauthorizedStaff = (s: { settings: SettingsState }) =>
  normalizeSettingsFlag(s.settings.site?.erp_appointments_warn_on_unauthorized_staff);
export const selectAppointmentsWarnOnUnauthorizedSpace = (s: { settings: SettingsState }) =>
  normalizeSettingsFlag(s.settings.site?.erp_appointments_warn_on_unauthorized_space);
export const selectAppointmentsWarningsEnabled = createSelector(
  [selectAppointmentsWarnOnUnauthorizedStaff, selectAppointmentsWarnOnUnauthorizedSpace],
  (staffFlag, spaceFlag) => resolveAppointmentsWarningsEnabled(staffFlag, spaceFlag),
);

const CMS_CAPABILITY_GROUP = 'cms';

export const selectCmsEnabled = createSelector(
  [selectPlanCapabilities, selectSiteApps],
  (planCapabilities, apps) => {
    const cmsCapabilities = planCapabilities[CMS_CAPABILITY_GROUP];
    if (cmsCapabilities && cmsCapabilities.builder !== undefined) {
      return Boolean(cmsCapabilities.builder);
    }

    return apps.includes(CMS_CAPABILITY_GROUP);
  },
);

export const selectCmsEshopBlocksEnabled = createSelector(
  [selectPlanCapabilities, selectSiteApps],
  (planCapabilities, apps) => {
    const cmsCapabilities = planCapabilities[CMS_CAPABILITY_GROUP];
    if (cmsCapabilities && cmsCapabilities.eshop_blocks !== undefined) {
      return Boolean(cmsCapabilities.eshop_blocks);
    }
    return apps.includes('eshop');
  },
);


export default settingsSlice.reducer;
